import { useState, useCallback } from 'react';

export interface PatientCoordinates {
  lat: number;
  lng: number;
}

export interface PatientLocationState {
  coords: PatientCoordinates | null;
  cep: string;
  addressLabel: string;
  isLocating: boolean;
  error: string | null;
  hasLocation: boolean;
}

const STORAGE_KEYS = {
  COORDS: 'medikei_patient_coords',
  CEP: 'medikei_patient_cep',
  LABEL: 'medikei_patient_address_label',
};

export function usePatientLocation() {
  const [coords, setCoords] = useState<PatientCoordinates | null>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.COORDS);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const [cep, setCepState] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEYS.CEP) || '';
  });

  const [addressLabel, setAddressLabel] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEYS.LABEL) || '';
  });

  const [isLocating, setIsLocating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sincroniza persistência de CEP
  const setCep = useCallback(async (newCep: string) => {
    const cleaned = newCep.replace(/\D/g, '');
    setCepState(cleaned);
    setError(null);

    if (cleaned.length >= 8) {
      localStorage.setItem(STORAGE_KEYS.CEP, cleaned);
      try {
        const res = await fetch(`https://viacep.com.br/ws/${cleaned}/json/`);
        if (res.ok) {
          const data = await res.json();
          if (!data.erro) {
            const label = [data.bairro, data.localidade, data.uf].filter(Boolean).join(', ');
            setAddressLabel(label);
            localStorage.setItem(STORAGE_KEYS.LABEL, label);
            return;
          }
        }
      } catch {
        // Fallback silencioso se offline
      }
    } else if (cleaned.length === 0) {
      localStorage.removeItem(STORAGE_KEYS.CEP);
      if (!coords) {
        setAddressLabel('');
        localStorage.removeItem(STORAGE_KEYS.LABEL);
      }
    }
  }, [coords]);

  // Ativação da Geolocalização GPS do Navegador
  const requestGpsLocation = useCallback((): Promise<PatientCoordinates | null> => {
    return new Promise((resolve) => {
      if (typeof window === 'undefined' || !navigator.geolocation) {
        const msg = 'Geolocalização não é suportada pelo seu navegador.';
        setError(msg);
        resolve(null);
        return;
      }

      setIsLocating(true);
      setError(null);

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const newCoords: PatientCoordinates = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setCoords(newCoords);
          localStorage.setItem(STORAGE_KEYS.COORDS, JSON.stringify(newCoords));
          setIsLocating(false);

          // Tenta resolver nome amigável de cidade/bairro via reverse geocode
          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${newCoords.lat}&lon=${newCoords.lng}&zoom=14&addressdetails=1`,
              { headers: { 'Accept-Language': 'pt-BR' } }
            );
            if (res.ok) {
              const data = await res.json();
              const suburb = data.address?.suburb || data.address?.neighbourhood || data.address?.city_district;
              const city = data.address?.city || data.address?.town || data.address?.municipality;
              const state = data.address?.state_code || data.address?.state;
              const label = [suburb, city, state].filter(Boolean).join(', ');
              if (label) {
                setAddressLabel(label);
                localStorage.setItem(STORAGE_KEYS.LABEL, label);
              } else {
                setAddressLabel('Localização atual GPS detectada');
              }
            } else {
              setAddressLabel('Localização atual GPS detectada');
            }
          } catch {
            setAddressLabel('Localização atual GPS detectada');
          }

          resolve(newCoords);
        },
        (err) => {
          setIsLocating(false);
          let message = 'Não foi possível obter sua localização.';
          if (err.code === err.PERMISSION_DENIED) {
            message = 'Permissão de GPS negada. Digite seu CEP para encontrar médicos próximos.';
          } else if (err.code === err.TIMEOUT) {
            message = 'Tempo limite esgotado ao buscar GPS. Tente novamente ou use seu CEP.';
          }
          setError(message);
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    });
  }, []);

  const clearLocation = useCallback(() => {
    setCoords(null);
    setCepState('');
    setAddressLabel('');
    setError(null);
    localStorage.removeItem(STORAGE_KEYS.COORDS);
    localStorage.removeItem(STORAGE_KEYS.CEP);
    localStorage.removeItem(STORAGE_KEYS.LABEL);
  }, []);

  return {
    coords,
    cep,
    addressLabel,
    isLocating,
    error,
    hasLocation: Boolean(coords || (cep && cep.length >= 8)),
    requestGpsLocation,
    setCep,
    clearLocation,
  };
}
